import {
  createAuth0Client,
  type Auth0Client,
  type RedirectLoginOptions,
  type User,
} from '@auth0/auth0-spa-js';

import { getAuth0EnvConfig } from '@shared/config/auth0';

/// `loginWithRedirect` に渡し、 callback で復元する deep-link 情報。
/// 元々 sessionStore のローカル型だったが、 client / store / auth0-service が
/// 参照するため shared に集約する。
export interface BootstrapAppState {
  returnTo: string;
}

/// access token の透過更新に失敗し、 明示的な再ログイン (loginWithRedirect) が
/// 必要になったときに `window` 上で発火する CustomEvent 名。
/// `shared` は「判断を持たずイベントを出すだけ」 の方針なので (NAVIGATE_EVENT と
/// 同流儀)、 購読・フロー判断は features / app 層に置く。
export const SESSION_EXPIRED_EVENT = 'lpcrm:session-expired';

/// Auth0 SPA SDK の薄いラッパ singleton。 NyaN の `shared/lib/auth0.svelte.ts`
/// と同様、 アプリ全体で 1 インスタンスを共有し、 初期化は非同期で 1 回だけ走らせる。
/// 状態 (user / isAuthenticated) は持たず、 sessionStore 側で管理する。
export class Auth0Service {
  private client: Auth0Client | null = null;
  private initPromise: Promise<Auth0Client> | null = null;

  async initialize(): Promise<void> {
    await this.ensureClient();
  }

  /// redirect 後のコールバックを消化し、 ログイン開始時に渡した `appState`
  /// (deep-link 復元用の `returnTo`) を上位へ返す。
  async handleRedirectCallback(): Promise<{ appState?: BootstrapAppState }> {
    const client = await this.ensureClient();
    const result = await client.handleRedirectCallback<BootstrapAppState>();
    return { appState: result.appState };
  }

  async isAuthenticated(): Promise<boolean> {
    const client = await this.ensureClient();
    return client.isAuthenticated();
  }

  async getUser(): Promise<User | undefined> {
    const client = await this.ensureClient();
    return client.getUser();
  }

  async getToken(): Promise<string> {
    const client = await this.ensureClient();
    return client.getTokenSilently();
  }

  async loginWithRedirect(options?: RedirectLoginOptions): Promise<void> {
    const client = await this.ensureClient();
    await client.loginWithRedirect(options);
  }

  async logout(): Promise<void> {
    const client = await this.ensureClient();
    await client.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }

  private async ensureClient(): Promise<Auth0Client> {
    if (this.client) return this.client;
    if (!this.initPromise) {
      const cfg = getAuth0EnvConfig();
      this.initPromise = createAuth0Client({
        domain: cfg.domain,
        clientId: cfg.clientId,
        authorizationParams: {
          audience: cfg.audience,
          redirect_uri: window.location.origin,
        },
        useRefreshTokens: true,
        // localhost dev は Auth0 上 "Verifiable First-Party" 扱いされず
        // skipConsentForVerifiableFirstPartyClients が効かない。memory cache だと
        // SPA ナビゲーション中の silent renew で iframe authorize が走り、
        // 都度 consent ダイアログがループする。localStorage に置いて
        // refresh token も含めて永続化し、silent renew 自体を抑止する。
        cacheLocation: 'localstorage',
      });
    }
    this.client = await this.initPromise;
    return this.client;
  }
}

export const auth0Service = new Auth0Service();
